/**
 * CalendarRequestFormModal Component
 * Modal for creating and editing calendar requests
 */

import { useState, useEffect } from "react";
import type { UUID } from "../../types/common.types";
import type {
  CalendarRequest,
  CreateCalendarRequestDTO,
  UpdateCalendarRequestDTO,
} from "../../types/calendarRequest.types";
import { supabase } from "../../services/supabase";
import { fetchAllRows } from "../../utils/fetchAllRows";
import { TIMEZONE_OPTIONS } from "../../utils/constants";

interface CalendarRequestFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (
    input: CreateCalendarRequestDTO | UpdateCalendarRequestDTO,
  ) => Promise<void>;
  request?: CalendarRequest | null;
}

interface Profile {
  id: UUID;
  full_name: string;
}

interface Organization {
  id: UUID;
  name: string;
}

interface Location {
  id: UUID;
  name: string;
  city?: string;
  state?: string;
}

interface Contact {
  id: UUID;
  first_name: string;
  last_name: string;
}

export default function CalendarRequestFormModal({
  isOpen,
  onClose,
  onSave,
  request,
}: CalendarRequestFormModalProps) {
  // Form fields
  const [description, setDescription] = useState("");
  const [requestDate, setRequestDate] = useState("");
  const [requestTime, setRequestTime] = useState("");
  const [timezone, setTimezone] = useState("");
  const [organizationId, setOrganizationId] = useState<UUID | "">("");
  const [locationId, setLocationId] = useState<UUID | "">("");
  const [contactId, setContactId] = useState<UUID | "">("");
  const [assignedTo, setAssignedTo] = useState<UUID | "">("");
  const [notes, setNotes] = useState("");

  // Data
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [orgSearch, setOrgSearch] = useState("");
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);

  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load profiles on mount
  useEffect(() => {
    if (isOpen) {
      fetchProfiles();
      fetchOrganizations();
    }
  }, [isOpen]);

  // Load locations and contacts when org changes
  useEffect(() => {
    if (organizationId) {
      fetchLocations(organizationId as UUID);
      fetchContacts(organizationId as UUID);
    } else {
      setLocations([]);
      setContacts([]);
      setLocationId("");
      setContactId("");
    }
  }, [organizationId]);

  // Initialize form when editing
  useEffect(() => {
    if (request) {
      setDescription(request.description || "");
      setRequestDate(request.request_date || "");
      setRequestTime(request.request_time || "");
      setTimezone(request.timezone || "");
      setOrganizationId(request.organization_id || "");
      setLocationId(request.location_id || "");
      setContactId(request.contact_id || "");
      setAssignedTo(request.assigned_to || "");
      setNotes(request.notes || "");
    } else {
      resetForm();
    }
    setErrors({});
  }, [request, isOpen]);

  const resetForm = () => {
    setDescription("");
    setRequestDate(new Date().toISOString().split("T")[0]);
    setRequestTime("");
    setTimezone("");
    setOrganizationId("");
    setLocationId("");
    setContactId("");
    setAssignedTo("");
    setNotes("");
    setOrgSearch("");
    setShowOrgDropdown(false);
  };

  const fetchProfiles = async () => {
    const { data, error } = await supabase
      .from("profiles")
      .select("id, full_name")
      .neq("role", "readonly") // a read-only viewer is never an assignee (owner 2026-11-03)
      .order("full_name");

    if (!error && data) {
      setProfiles(data);
    }
  };

  const fetchOrganizations = async () => {
    // Page through ALL orgs — a single .limit() cannot exceed PostgREST's
    // 1000-row cap, so the dropdown would silently truncate. See fetchAllRows.
    try {
      const rows = await fetchAllRows<Organization>((from, to) =>
        supabase
          .from("organizations")
          .select("id, name")
          .eq("is_archived", false)
          .order("name")
          .range(from, to),
      );
      setOrganizations(rows);
    } catch (error) {
      console.error("Error loading organizations:", error);
    }
  };

  const fetchLocations = async (orgId: UUID) => {
    const { data, error } = await supabase
      .from("locations")
      .select("id, name, city, state")
      .eq("organization_id", orgId)
      .order("name");

    if (!error && data) {
      setLocations(data);
    }
  };

  const fetchContacts = async (orgId: UUID) => {
    const { data, error } = await supabase
      .from("contacts")
      .select("id, first_name, last_name")
      .eq("organization_id", orgId)
      .order("last_name")
      .limit(100);

    if (!error && data) {
      setContacts(data);
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!description.trim()) {
      newErrors.description = "Description is required";
    }
    if (!requestDate) {
      newErrors.requestDate = "Date is required";
    }
    if (requestTime && !timezone) {
      newErrors.timezone = "Timezone is required when time is set";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      return;
    }

    setIsSaving(true);
    try {
      const input: CreateCalendarRequestDTO | UpdateCalendarRequestDTO = {
        description: description.trim(),
        request_date: requestDate,
        request_time: requestTime || undefined,
        timezone: requestTime ? timezone : undefined,
        organization_id: organizationId || undefined,
        location_id: locationId || undefined,
        contact_id: contactId || undefined,
        assigned_to: assignedTo || undefined,
        notes: notes.trim() || undefined,
      };

      await onSave(input);
      onClose();
    } catch (error) {
      console.error("Error saving calendar request:", error);
      setErrors({ submit: "Failed to save request. Please try again." });
    } finally {
      setIsSaving(false);
    }
  };

  const selectedOrgName =
    organizations.find((o) => o.id === organizationId)?.name || "";

  const filteredOrgs = organizations.filter((org) =>
    org.name.toLowerCase().includes(orgSearch.toLowerCase()),
  );

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              {request ? "Edit Calendar Request" : "New Calendar Request"}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  errors.description ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Describe what needs to be done..."
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.description}
                </p>
              )}
            </div>

            {/* Date and Time */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={requestDate}
                  onChange={(e) => setRequestDate(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.requestDate ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.requestDate && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.requestDate}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Time
                </label>
                <input
                  type="time"
                  value={requestTime}
                  onChange={(e) => setRequestTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Timezone{" "}
                  {requestTime && <span className="text-red-500">*</span>}
                </label>
                <select
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.timezone ? "border-red-500" : "border-gray-300"
                  }`}
                  disabled={!requestTime}
                >
                  <option value="">Select timezone...</option>
                  {TIMEZONE_OPTIONS.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
                {errors.timezone && (
                  <p className="mt-1 text-sm text-red-600">{errors.timezone}</p>
                )}
              </div>
            </div>

            {/* Organization */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Organization
              </label>
              {organizationId ? (
                <div className="flex items-center justify-between bg-primary-50 px-3 py-2 rounded-md">
                  <span className="text-sm text-primary-800">
                    {selectedOrgName}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setOrganizationId("");
                      setOrgSearch("");
                    }}
                    className="text-primary-600 hover:text-primary-800 text-sm"
                  >
                    Clear
                  </button>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    value={orgSearch}
                    onChange={(e) => {
                      setOrgSearch(e.target.value);
                      setShowOrgDropdown(true);
                    }}
                    onFocus={() => setShowOrgDropdown(true)}
                    placeholder="Search organizations..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  {showOrgDropdown && filteredOrgs.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg">
                      {filteredOrgs.slice(0, 50).map((org) => (
                        <button
                          key={org.id}
                          type="button"
                          onClick={() => {
                            setOrganizationId(org.id);
                            setOrgSearch("");
                            setShowOrgDropdown(false);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm text-gray-700"
                        >
                          {org.name}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Location (when org selected) */}
            {organizationId && locations.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location
                </label>
                <select
                  value={locationId}
                  onChange={(e) => setLocationId(e.target.value as UUID)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Select location...</option>
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name}
                      {loc.city && loc.state
                        ? ` (${loc.city}, ${loc.state})`
                        : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Contact (when org selected) */}
            {organizationId && contacts.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact
                </label>
                <select
                  value={contactId}
                  onChange={(e) => setContactId(e.target.value as UUID)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Select contact...</option>
                  {contacts.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.first_name} {c.last_name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Assigned To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Assigned To
              </label>
              <select
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value as UUID)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Unassigned</option>
                {profiles.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.full_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Additional notes..."
              />
            </div>

            {/* Submit Error */}
            {errors.submit && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{errors.submit}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Saving...
                </>
              ) : request ? (
                "Update Request"
              ) : (
                "Create Request"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
